import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { ArticleDto, DimensionType, IArticleDto, LabelDto } from '../../api/ClientApi';
import { RootState } from '../reducers';
import { myAssert } from '../../common/console';
import { WarehouseClient } from '../../api/Extensions';
import { addMultipleArticleLabelToRedux, setLabelEntities } from './labelActions';
import { LabelEntity } from '../reducers/labelReducer';

export const createNewEmptyArticle = createAction('article/createNewEmptyArticle');
export const clearArticle = createAction('article/clearArticle');

export const updateArticleTitle = createAction<string>('article/updateArticleTitle');
export const updateArticleManufacturer = createAction<string>('article/updateArticleManufacturer');
export const updateArticleDimensionType = createAction<DimensionType | undefined>('article/updateArticleDimensionType');
export const updateArticleInnerCode = createAction<string>('article/updateArticleInnerCode');
export const updateArticleOutterCode = createAction<string>('article/updateArticleOutterCode');
export const updateArticlePrice = createAction<number>('article/updateArticlePrice');
export const updateArticleDescription = createAction<string>('article/updateArticleDescription');

export const createNewArticle = createAction<IArticleDto>('article/createNewArticle');
export const removeLabelFromArticle = createAction<number>('article/removeLabelFromArticle');

export const setResponseMessage = createAction<string>('article/setResponseMessage');

export const saveArticle = createAsyncThunk<ArticleDto, void>(
    'article/saveArticle',
    async (payload: void, thunkAPI) => {
        const rootState = thunkAPI.getState() as RootState;
        const articleState = rootState.article;

        const article = articleState.article;
        const labels = rootState.label.labelEntities;

        myAssert(() => article !== null);

        if (article === null) {
            throw new Error();
        }

        const newArticle = new ArticleDto();
        newArticle.init(article);

        const articleId: number | undefined = article.id;
        let savedArticle: ArticleDto | undefined;

        if (!articleId || articleId <= 0) {
            const addArticlePromise = WarehouseClient.addNewArticle(article as ArticleDto).then(
                async (res) => {
                    let savedLabels: LabelDto[] = [];
                    const savedLabelsPromises: Promise<LabelDto>[] = [];
                    labels?.map((label) => {
                        const currLabel = label.label;

                        const addLabelPromise = WarehouseClient.addNewArticleLabel(res.id ?? 0, currLabel);

                        savedLabelsPromises.push(addLabelPromise);
                    });

                    savedLabels = await Promise.all(savedLabelsPromises);

                    res.labels = savedLabels;

                    return res;
                },
                (err) => {
                    console.log(err);
                    const message =
                        err.MessageCode === 'EXCEPTION_OUTTERCODE'
                            ? 'Šifra artikla proizvođača već postoji'
                            : err.MessageCode === 'EXCEPTION_INNERCODE'
                              ? 'Interni kod proizvoda već postoji'
                              : '';

                    thunkAPI.dispatch(setResponseMessage(message));
                    throw Error(message);
                },
            );

            savedArticle = await addArticlePromise;
        } else {
            myAssert(() => newArticle.id !== undefined);

            if (newArticle.id === undefined) {
                throw new Error();
            }

            const currArticleLabels = newArticle.labels;

            let savedLabels: LabelDto[] = [];

            const savedLabelsPromises: Promise<LabelDto>[] = [];

            labels
                ?.filter((label) => !currArticleLabels?.find((l) => l.id === label.label?.id))
                .map((label) => {
                    const addLabelPromise = WarehouseClient.addNewArticleLabel(newArticle.id ?? 0, label.label);

                    savedLabelsPromises.push(addLabelPromise);
                });

            savedLabels = await Promise.all(savedLabelsPromises);

            thunkAPI.dispatch(addMultipleArticleLabelToRedux(savedLabels));

            newArticle.labels = [];

            const articlePromise = WarehouseClient.updateArticle(newArticle.id, newArticle as ArticleDto);

            savedArticle = await articlePromise;
        }

        return savedArticle;
    },
    {
        condition: (payload: void, thunkAPI: { getState: any }) => {
            const articleState = (thunkAPI.getState() as RootState).article;

            return articleState.article !== null;
        },
    },
);

export const importArticles = createAsyncThunk<ArticleDto[]>('article/importArticles', async (payload, thunkAPI) => {
    return WarehouseClient.getAllArticles();
});

export const importArticle = createAsyncThunk<ArticleDto, number>(
    'article/importArticle',
    async (payload, thunkAPI) => {
        return WarehouseClient.getArticleById(payload).then((res) => {
            const labelEntities: LabelEntity[] = [];
            res.labels?.map((label) => {
                const index = labelEntities.length === 0 ? 1 : labelEntities[labelEntities.length - 1].index + 1;
                const newLabelEntity: LabelEntity = {
                    index,
                    label,
                };
                labelEntities.push(newLabelEntity);
            });

            thunkAPI.dispatch(setLabelEntities(labelEntities));

            return res;
        });
    },
);
